import {Label as LabelPrimitive} from 'radix-ui';
import * as React from 'react';

import {cn} from '@/utils/ui';

export type InputProps = React.InputHTMLAttributes<HTMLInputElement>;

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({className, type, ...props}, ref) => {
    return (
      <input
        type={type}
        className={cn(
          'flex h-11 w-full rounded-sharp border border-input bg-white px-4 py-2 type-body1',
          'file:border-0 file:bg-transparent file:text-sm file:font-medium',
          'placeholder:text-neutral-400',
          'focus-visible:border-secondary-400 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none',
          'aria-invalid:border-danger-600 aria-invalid:focus-visible:border-danger-600 aria-invalid:focus-visible:ring-danger-600',
          'disabled:cursor-not-allowed disabled:border-neutral-300 disabled:bg-neutral-100 disabled:text-neutral-500 disabled:placeholder:text-neutral-400',
          className
        )}
        ref={ref}
        {...props}
      />
    );
  }
);
Input.displayName = 'Input';

const InputLabel = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>
>(({className, ...props}, ref) => (
  <LabelPrimitive.Root
    ref={ref}
    className={cn(
      'type-label peer-disabled:cursor-not-allowed peer-disabled:text-neutral-500',
      className
    )}
    {...props}
  />
));
InputLabel.displayName = LabelPrimitive.Root.displayName;

const InputItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({className, ...props}, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        'space-y-1 [&:has(:disabled)>label]:cursor-not-allowed [&:has(:disabled)>label]:text-neutral-500',
        className
      )}
      {...props}
    />
  );
});
InputItem.displayName = 'InputItem';

const InputDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({className, ...props}, ref) => {
  return (
    <p
      ref={ref}
      className={cn('text-2xs font-medium text-secondary-dark', className)}
      {...props}
    />
  );
});
InputDescription.displayName = 'InputDescription';

const InputMessage = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({className, children, ...props}, ref) => {
  return (
    <p
      ref={ref}
      className={cn('text-2xs font-medium text-danger-600', className)}
      {...props}
    >
      {children}
    </p>
  );
});
InputMessage.displayName = 'InputMessage';

export {Input, InputDescription, InputItem, InputLabel, InputMessage};
